import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useActivePractices } from '../../hooks/usePractices';
import styles from './WorkspacePracticeSwitcher.module.css';

interface WorkspacePracticeSwitcherProps {
  practiceId: string;
  practiceName: string;
  /** The workspace tab to land on in the next practice; omitted for Overview. */
  tab?: string;
}

/**
 * The practice name in the workspace header, as a jump to any other active
 * practice — no round trip through the Practices list. The tab carries over;
 * location and tab-local params do not, since they belong to this practice.
 */
export function WorkspacePracticeSwitcher({
  practiceId,
  practiceName,
  tab,
}: WorkspacePracticeSwitcherProps) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { data: practices, isLoading } = useActivePractices();

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const all = practices ?? [];
    return q ? all.filter((p) => p.name.toLowerCase().includes(q)) : all;
  }, [practices, search]);

  useEffect(() => {
    if (isOpen) {
      const id = requestAnimationFrame(() => searchInputRef.current?.focus());
      return () => cancelAnimationFrame(id);
    }
    setSearch('');
  }, [isOpen]);

  const select = (id: string) => {
    setIsOpen(false);
    if (id === practiceId) return;
    navigate(`/agency/practices/${id}${tab ? `?tab=${encodeURIComponent(tab)}` : ''}`);
  };

  return (
    <div className={styles.wrapper}>
      <button
        type="button"
        className={styles.trigger}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        title="Switch practice"
        onClick={() => setIsOpen((open) => !open)}
      >
        <h1 className={styles.title}>{practiceName}</h1>
        <ChevronDown size={18} className={styles.chevron} aria-hidden />
      </button>

      {isOpen && (
        <>
          <div className={styles.backdrop} onClick={() => setIsOpen(false)} />
          <div className={styles.dropdown}>
            <div className={styles.searchRow}>
              <Search size={14} className={styles.searchIcon} aria-hidden />
              <input
                ref={searchInputRef}
                type="text"
                className={styles.searchInput}
                placeholder="Search practices…"
                aria-label="Search practices"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setIsOpen(false);
                  if (e.key === 'Enter' && filtered.length > 0) select(filtered[0].id);
                }}
              />
            </div>
            <div className={styles.list} role="listbox" aria-label="Active practices">
              {isLoading && <div className={styles.emptyState}>Loading practices…</div>}
              {!isLoading && filtered.length === 0 && (
                <div className={styles.emptyState}>
                  {search ? `No practices match “${search}”` : 'No other active practices'}
                </div>
              )}
              {filtered.map((practice) => (
                <button
                  key={practice.id}
                  type="button"
                  role="option"
                  aria-selected={practice.id === practiceId}
                  className={styles.item}
                  onClick={() => select(practice.id)}
                >
                  <span className={styles.itemName}>{practice.name}</span>
                  {practice.id === practiceId && <Check size={14} className={styles.check} />}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
